import { FC, useEffect, useState, useRef } from 'react'

const googleClientId = (import.meta as any).env.VITE_GOOGLE_CLIENT_ID

// 生成 nonce 和 hashedNonce
// Google 接收 hashedNonce，Supabase 接收原始 nonce
const generateNonce = async (): Promise<[string, string]> => {
    const nonce = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))))
    const encoder = new TextEncoder()
    const encodedNonce = encoder.encode(nonce)
    const hashBuffer = await crypto.subtle.digest('SHA-256', encodedNonce)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashedNonce = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
    return [nonce, hashedNonce]
}

interface GoogleOneTapProps {
    onSuccess?: () => void
    user?: any
    loading?: boolean
    signInWithGoogleIdToken: (token: string, nonce?: string) => Promise<{ success: boolean }>
}

export const GoogleOneTap: FC<GoogleOneTapProps> = ({ onSuccess, user, loading, signInWithGoogleIdToken }) => {
    const [scriptLoaded, setScriptLoaded] = useState(false)
    const [attempted, setAttempted] = useState(false)
    const initializedRef = useRef(false)
    const nonceRef = useRef<string>('')

    // 加载 Google GSI 脚本
    useEffect(() => {
        if (!googleClientId || googleClientId === 'your_google_client_id.apps.googleusercontent.com') {
            return
        }

        // 检查是否已经加载过
        if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
            setScriptLoaded(true)
            return
        }

        const script = document.createElement('script')
        script.src = 'https://accounts.google.com/gsi/client'
        script.async = true
        script.defer = true
        script.onload = () => setScriptLoaded(true)
        script.onerror = () => console.error('Failed to load Google GSI script')
        document.body.appendChild(script)

        // 不在 cleanup 中移除脚本，避免重复加载
    }, [])

    // 初始化 Google One Tap
    useEffect(() => {
        if (!scriptLoaded || !googleClientId || user || loading || attempted) {
            // 如果用户已登录或正在加载，取消 One Tap
            if ((user || loading || attempted) && (window as any).google?.accounts?.id) {
                (window as any).google.accounts.id.cancel()
            }
            return
        }

        // 防止重复初始化
        if (initializedRef.current) {
            return
        }

        const initOneTap = async () => {
            try {
                const [nonce, hashedNonce] = await generateNonce()
                nonceRef.current = nonce

                const google = (window as any).google
                if (!google?.accounts?.id) {
                    return
                }

                google.accounts.id.initialize({
                    client_id: googleClientId,
                    callback: async (response: any) => {
                        if (response.credential) {
                            setAttempted(true)
                            const { success } = await signInWithGoogleIdToken(
                                response.credential,
                                nonceRef.current
                            )
                            if (success && onSuccess) {
                                onSuccess()
                            }
                        }
                    },
                    nonce: hashedNonce,
                    cancel_on_tap_outside: true,
                    use_fedcm_for_prompt: true,
                })

                google.accounts.id.prompt()
                initializedRef.current = true
            } catch (err) {
                console.error('Failed to initialize Google One Tap:', err)
            }
        }

        initOneTap()

        return () => {
            if ((window as any).google?.accounts?.id) {
                (window as any).google.accounts.id.cancel()
            }
            initializedRef.current = false
        }
    }, [scriptLoaded, user, loading, attempted, signInWithGoogleIdToken, onSuccess])

    return null
}
